import { ChatbotConfig } from '../config/chatbot-config';
import Cookies from 'js-cookie';

export const EsterService = {
  getConfig: () => ChatbotConfig,

  getAuthToken: () => {
    // Try to get token from cookies first (common for JWT)
    const tokenFromCookie = Cookies.get('token') || Cookies.get('jwt') || Cookies.get('access_token');
    if (tokenFromCookie) {
      return tokenFromCookie;
    }
    
    // Try localStorage
    const tokenFromLocalStorage = localStorage.getItem('token') || localStorage.getItem('jwt') || localStorage.getItem('access_token');
    if (tokenFromLocalStorage) {
      return tokenFromLocalStorage;
    }
    
    // Try sessionStorage
    const tokenFromSession = sessionStorage.getItem('token') || sessionStorage.getItem('jwt') || sessionStorage.getItem('access_token');
    if (tokenFromSession) {
      return tokenFromSession;
    }
    
    // If no token found, return null (backend should handle 401)
    return null;
  },

  getHeaders: () => {
    const config = EsterService.getConfig();
    const token = EsterService.getAuthToken();
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  },

  initializeEsterChat: async () => {
    const config = EsterService.getConfig();

    if (!config.assistant.assistantId) {
      throw new Error('Assistant ID not configured. Please add REACT_APP_OPENAI_ASSISTANT_ID to your .env file and restart the dev server.');
    }

    try {
      const response = await fetch(`${config.api.baseUrl}/threads`, {
        method: 'POST',
        headers: EsterService.getHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = errorData.error || `Backend API error: ${response.status} ${response.statusText}`;
        if (response.status === 401) {
          errorMessage = `Authentication failed (401). Please log in again.`;
        }
        throw new Error(errorMessage);
      }

      const thread = await response.json();

      return {
        threadId: thread.threadId,
        messages: [],
        sessionId: thread.sessionId || thread.threadId,
        isActive: thread.isActive !== undefined ? thread.isActive : true,
        createdAt: thread.createdAt || new Date().toISOString()
      };
    } catch (error) {
      throw error;
    }
  },

  addUserMessage: async (threadId, message) => {
    const config = EsterService.getConfig();
    
    if (!threadId || threadId === 'undefined') {
      throw new Error('Invalid thread ID. Thread may not have been initialized properly.');
    }

    try {
      const response = await fetch(`${config.api.baseUrl}/threads/${threadId}/messages`, {
        method: 'POST',
        headers: EsterService.getHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          message: message
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || 
          `Failed to add message: ${response.status} ${response.statusText}`
        );
      }

      return response.json();
    } catch (error) {
      throw error;
    }
  },

  runAssistant: async (threadId) => {
   
    const config = EsterService.getConfig();

    try {
      // Backend doesn't support non-streaming runs with polling
      // This method is kept for compatibility but will not work with backend API
      throw new Error('Non-streaming assistant runs are not supported. Please use runAssistantStream instead.');
    } catch (error) {
      console.error('[Ester] Failed to run assistant:', error);
      throw error;
    }
  },

  runAssistantStream: async (threadId, onChunk, onComplete, onError) => {
    const config = EsterService.getConfig();

    try {
      const response = await fetch(`${config.api.baseUrl}/threads/${threadId}/runs`, {
        method: 'POST',
        headers: {
          ...EsterService.getHeaders(),
          'Accept': 'text/event-stream'
        },
        credentials: 'include',
        body: JSON.stringify({
          assistantId: config.assistant.assistantId
        })
      });

      // Check if response is OK before trying to read stream
      if (!response.ok) {
        // Try to get error message from response body
        let errorMessage = `Failed to create streaming run: ${response.status} ${response.statusText}`;
        try {
          // Check if response is JSON
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } else {
            // Try to read as text
            const errorText = await response.text();
            if (errorText) {
              try {
                const errorData = JSON.parse(errorText);
                errorMessage = errorData.error || errorMessage;
              } catch {
                errorMessage = errorText || errorMessage;
              }
            }
          }
        } catch (parseError) {
          // If we can't parse the error, use the default message
          console.error('[Ester] Error parsing error response:', parseError);
        }
        throw new Error(errorMessage);
      }

      // Verify we have a readable stream
      if (!response.body) {
        throw new Error('Response body is not readable. Server may not support streaming.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = null;

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            // Stream ended normally
            onComplete();
            break;
          }

          // Decode the chunk and add to buffer
          buffer += decoder.decode(value, { stream: true });
          
          const lines = buffer.split(/\r?\n/);
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmedLine = line.trim();
            
            if (trimmedLine === '') {
              currentEvent = null;
              continue;
            }
            
            if (trimmedLine.startsWith('event:')) {
              currentEvent = trimmedLine.slice(6).trim();
              continue;
            }
            
            
            if (trimmedLine.startsWith('data:')) {
              const data = trimmedLine.slice(5).trim();
              
            if (data.trim() === '[DONE]') {
                onComplete();
                return;
              }
              try {
                const parsed = JSON.parse(data);

                const isMessageDelta =
                  parsed.object === 'thread.message.delta' ||
                  currentEvent === 'thread.message.delta';

                if (isMessageDelta) {
                  const contentArray = parsed.delta?.content;
                  
                  if (contentArray && Array.isArray(contentArray) && contentArray.length > 0) {
                    const delta = contentArray[0];
                    
                    if (delta?.type === 'text' && delta?.text?.value) {
                      onChunk(delta.text.value);
                    }
                  }
                } 
                
                else if (currentEvent === 'error') {
                  const errorMsg = parsed.error || 'An error occurred';
                  onError(new Error(errorMsg));
                  return;
                } 
                
                else if (currentEvent === 'thread.run.failed') {
                  const errorMsg = parsed.last_error?.message || parsed.error || 'Run failed';
                  onError(new Error(errorMsg));
                  return;
                }
              
                else if (currentEvent === 'thread.run.cancelled') {
                  onError(new Error('Run was cancelled'));
                  return;
                } 
                
                else if (currentEvent === 'thread.run.expired') {
                  onError(new Error('Run expired'));
                  return;
                } 
                
                else if (
                  currentEvent === 'done' ||
                  currentEvent === 'thread.run.completed' ||
                  currentEvent === 'thread.message.completed' ||
                  parsed.object === 'thread.run.completed' ||
                  parsed.object === 'thread.message.completed'
                ) {
                  onComplete();
                  return;
                }
                
                else if (!currentEvent && parsed.delta?.content) {
                  const contentArray = parsed.delta.content;
                  if (Array.isArray(contentArray) && contentArray.length > 0) {
                    const delta = contentArray[0];
                    if (delta?.type === 'text' && delta?.text?.value) {
                      onChunk(delta.text.value);
                    }
                  }
                }
              } catch (parseError) {
                
                if (data.trim() && data.trim() !== '[DONE]') {
                  console.debug('[Ester] Could not parse SSE data as JSON:', data.substring(0, 100));
                }
              }
            }
          }
        }
      } catch (streamError) {
        // Handle stream reading errors
        console.error('[Ester] Stream reading error:', streamError);
        onError(new Error(`Stream error: ${streamError.message}`));
      } finally {
        // Ensure reader is released
        try {
          reader.releaseLock();
        } catch (e) {
          // Reader may already be released
        }
      }
    } catch (error) {
      // Handle fetch or other errors
      console.error('[Ester] Stream fetch error:', error);
      onError(error);
    }
  },

  getThreadMessages: async (threadId, limit = 20) => {
    const config = EsterService.getConfig();

    try {
      const response = await fetch(
        `${config.api.baseUrl}/threads/${threadId}/messages?limit=${limit}`,
        {
          method: 'GET',
          headers: EsterService.getHeaders(),
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to retrieve messages');
      }

      const data = await response.json();
      // Backend returns array directly, not wrapped in data property
      return Array.isArray(data) ? data : (data.data || []);
    } catch (error) {
      throw error;
    }
  },

  validateConfig: () => {
    const config = EsterService.getConfig();
    const token = EsterService.getAuthToken();
    
    // Check if user is logged in (sessionStorage has user data)
    // This is the primary auth check since the app uses cookie-based authentication
    const user = sessionStorage.getItem('user');
    const isLoggedIn = user !== null;

    if (!isLoggedIn && !token) {
      return {
        valid: false,
        message: 'Authentication token not found. Please log in to use the chat feature.'
      };
    }

    if (!config.assistant.assistantId || config.assistant.assistantId === 'asst_XXXXXXX') {
      return {
        valid: false,
        message: 'Assistant ID is not configured. Please add REACT_APP_OPENAI_ASSISTANT_ID to your .env file.'
      };
    }

    if (!config.assistant.assistantId.startsWith('asst_')) {
      return {
        valid: false,
        message: 'Invalid Assistant ID format. Assistant IDs should start with "asst_".'
      };
    }

    return {
      valid: true,
      message: 'Configuration is valid.'
    };
  }
};

export default EsterService;

